import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsSpecialContentSettingMetadata
} from '../../client-server-common';
import {
    DoppeActionCTATypeLinkOpenType,
    DoppeLinkSpec,
    doppeLinkSpecCreateDefault,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl
} from '../../client-server-common/types/doppe-action-cta-button';
import {bymoPageGetInnerNavigatablePageById} from '../../client-server-common/bymo-page-utils/bymo-page-utils';
import {DoppeDtoInnerPageType} from '../../client-server-common/types/doppe-dto-inner-page';
import {DoppeViewerPageType} from '../../client-server-common/viewer-pages/doppe-viewer-pages';


export namespace DoppeActionTypeLink {

    export interface ActionSettings {
        /**
         * Link specification - can be a URL string, a page reference, or an entity reference.
         * Previously was just a string URL, now supports DoppeLinkSpec for page/entity links.
         */
        url: DoppeLinkSpec;
        /**
         * How the link should open (new tab, same tab, or in-page modal).
         */
        openType: DoppeActionCTATypeLinkOpenType;
    }
}

export const doppeActionTypeLink = doppeActionTypeDefineMetadata<DoppeActionTypeLink.ActionSettings>({
    id: 'io.bymo.action/link',
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction({
        url: doppeLinkSpecCreateDefault(),
        openType: DoppeActionCTATypeLinkOpenType.SameTab,
    }),
    resolveMainLink: (actionSettings, _htmlSiteInfo, _viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        const url = actionSettings.url;
        
        // Handle simple URL string
        if (doppeLinkSpecIsUrl(url)) {
            return url;
        }
        
        // Handle Page link spec - resolve URL from page info using routes manager
        // This follows the same logic as doppeActionCtaButtonTypeResolveURL
        if (doppeLinkSpecIsPage(url) && bymoPageProps && viewerPagesRoutesManager) {
            const page = bymoPageGetInnerNavigatablePageById(url.pageId, bymoPageProps);
            if (page) {
                // Use routes manager to resolve paths (same as CTA button logic)
                switch (page.type) {
                    case DoppeDtoInnerPageType.Products:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.ProductsList
                        });
                    case DoppeDtoInnerPageType.BlogPosts:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.BlogPostsList
                        });
                    case DoppeDtoInnerPageType.Events:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.EventsList
                        });
                    case DoppeDtoInnerPageType.Bookings:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.ServicesList
                        });
                    case DoppeDtoInnerPageType.Content:
                    default:
                        return viewerPagesRoutesManager.contentPage({slug: page.slug});
                }
            }
        }
        
        // For entity links or when routes manager is not available, return null
        return null;
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            url: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            openType: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered
        },
        supportsActionCtaButtons: false,
        supportsContentSections: false
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: false,
    hasNoLockInnerCTA: true,
})